import { useMemo } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { CompareDatePicker, DatePicker, Select } from "@components/v2";
import { QUICK_SELECT_OPTIONS as COMPARE_DATE_RANGE_QUICK_SELECT_OPTIONS } from "@components/v2/compare-date-picker/constants";
import { QUICK_SELECT_OPTIONS as DATE_RANGE_QUICK_SELECT_OPTIONS } from "@components/v2/date-picker/constants";
import { InputButton } from "@components/v2/inputs/input-button";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { type User } from "@internal/user";
import {
  type CompareDateRangeV2,
  type DateRangeChoiceV4,
  dateRangeToInterval,
  type FixedDateInterval,
  type ITimeGranularityV2,
  parseDateInterval,
  TimeGranularityV2,
  todayLocalAsISODate,
} from "@north-beam/nb-common";

interface Props {
  dateRange: FixedDateInterval | DateRangeChoiceV4;
  compareDateRange: CompareDateRangeV2;
  timeGranularity: ITimeGranularityV2;
  timezone: User["timezone"];
  minDate?: string;
  maxDate?: string;
  minCompareDate?: string;
  maxCompareDate?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  buttonClassName?: string;
  onChangeDateRange: (val: FixedDateInterval | DateRangeChoiceV4) => void;
  onChangeCompareDateRange: (val: CompareDateRangeV2) => void;
  onChangeTimeGranularity: (val: ITimeGranularityV2) => void;
}

export const DateOptionsPicker = ({
  dateRange,
  compareDateRange,
  timeGranularity,
  timezone,
  minDate,
  maxDate,
  minCompareDate,
  maxCompareDate,
  disabled,
  size,
  buttonClassName,
  onChangeDateRange,
  onChangeCompareDateRange,
  onChangeTimeGranularity,
}: Props) => {
  const referenceDateRange = useMemo(() => {
    if (typeof dateRange === "string") {
      return parseDateInterval(
        dateRangeToInterval(dateRange),
        todayLocalAsISODate(),
      );
    }

    return { startDate: dateRange.startDate, endDate: dateRange.endDate };
  }, [dateRange]);

  const dateRangeLabel = useMemo(() => {
    if (!dateRange) {
      return null;
    }

    if (typeof dateRange === "string") {
      return DATE_RANGE_QUICK_SELECT_OPTIONS.find((e) => e.value === dateRange)
        ?.label;
    }

    return "Custom";
  }, [dateRange]);

  const compareDateRangeLabel = useMemo(() => {
    if (!compareDateRange) {
      return null;
    }

    if (compareDateRange.value !== "custom") {
      return COMPARE_DATE_RANGE_QUICK_SELECT_OPTIONS.find(
        (e) => e.value === compareDateRange.value,
      )?.label;
    }

    return "Custom";
  }, [compareDateRange]);

  return (
    <Popover>
      <PopoverButton
        as={InputButton}
        size={size}
        disabled={disabled}
        leftIcon={<i className="fa-regular fa-calendar" />}
        className={twMerge("w-60", buttonClassName)}
      >
        <span className="flex items-center gap-1 w-full flex-1 truncate">
          <span className="truncate">{dateRangeLabel}</span>
          <span className="text-xs text-v2-secondary">/</span>
          <span className="text-xs text-v2-secondary truncate">
            {TimeGranularityV2.getLabel(timeGranularity)}
          </span>
          <span className="text-xs text-v2-secondary">/</span>
          <span className="text-xs text-v2-secondary truncate">
            {compareDateRangeLabel}
          </span>
        </span>
      </PopoverButton>
      <PopoverPanel
        as="div"
        anchor={{ to: "bottom start", gap: 4 }}
        transition
        className={clsx(
          "z-[9999] border border-v2-secondary rounded-md elevation-2 bg-v2-primary",
          "origin-top transition duration-200 ease-out p-4 w-60",
          "data-[closed]:translate-y-1 data-[closed]:opacity-0",
        )}
      >
        <div className="mb-2">
          <div className="text-xs font-semibold mb-2">Date Range</div>
          <DatePicker
            className="w-full"
            value={dateRange}
            minDate={minDate}
            maxDate={maxDate}
            onChange={(val) => val && onChangeDateRange(val)}
          />
        </div>
        <div className="mb-2">
          <div className="text-xs font-semibold mb-2">Time Granularity</div>
          <Select
            className="w-full"
            buttonClassName="w-full"
            size="md"
            options={TimeGranularityV2.reactSelectOptions}
            value={timeGranularity}
            onChange={(val) => {
              if (val) {
                onChangeTimeGranularity(val);

                if (val === "hourly") {
                  onChangeDateRange("last7Days");
                  onChangeCompareDateRange({ value: "previousPeriod" });
                }
              }
            }}
          />
        </div>
        <div>
          <div className="text-xs font-semibold mb-2">Compare To</div>
          <CompareDatePicker
            className="w-full"
            minDate={minCompareDate}
            maxDate={maxCompareDate}
            value={compareDateRange}
            onChange={(val) => val && onChangeCompareDateRange(val)}
            referenceDateRange={referenceDateRange}
            timezone={timezone}
            timeGranularity={timeGranularity}
          />
        </div>
      </PopoverPanel>
    </Popover>
  );
};
